import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { 
  Brain, 
  TrendingUp, 
  AlertTriangle, 
  Target, 
  Zap,
  BarChart3,
  PieChart,
  Activity,
  DollarSign,
  Calendar,
  Clock,
  Users,
  Globe
} from 'lucide-react';
import { aiPredictionsService, PredictionData, FraudAlert, CashFlowPrediction } from '../../services/aiPredictions';

const AdvancedAnalytics: React.FC = () => {
  const { formatCurrency, countryConfig } = useLocalization();
  const [predictions, setPredictions] = useState<PredictionData[]>([]);
  const [fraudAlerts, setFraudAlerts] = useState<FraudAlert[]>([]);
  const [cashFlowPredictions, setCashFlowPredictions] = useState<CashFlowPrediction[]>([]);
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [patterns, setPatterns] = useState<any>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('7days');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedPeriod]);

  const loadAnalyticsData = async () => {
    setIsLoading(true);
    
    // Simulation d'un délai de chargement pour l'IA
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const days = selectedPeriod === '7days' ? 7 : selectedPeriod === '30days' ? 30 : 90;
    
    setPredictions(aiPredictionsService.generateVolumePredictions(days));
    setFraudAlerts(aiPredictionsService.detectFraudulentTransactions());
    setCashFlowPredictions(aiPredictionsService.generateCashFlowPredictions());
    setRecommendations(aiPredictionsService.generateRecommendations());
    setPatterns(aiPredictionsService.analyzeTransactionPatterns());
    
    setIsLoading(false);
  };

  const getRiskColor = (score: number) => {
    if (score >= 0.7) return 'text-red-600 bg-red-100';
    if (score >= 0.4) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'down': return <TrendingUp className="h-4 w-4 text-red-600 rotate-180" />;
      default: return <Activity className="h-4 w-4 text-blue-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="relative">
            <Brain className="h-16 w-16 text-[#63d400] mx-auto mb-4 animate-pulse" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-[#63d400] border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">IA en cours d'analyse...</h3>
          <p className="text-gray-600">Génération des prédictions et insights</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-lg mr-4">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Analytics Avancés & IA</h2>
            <p className="text-gray-600">Prédictions intelligentes et insights automatisés</p>
          </div>
        </div>
        
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#63d400] focus:border-transparent"
        >
          <option value="7days">7 jours</option>
          <option value="30days">30 jours</option>
          <option value="90days">90 jours</option>
        </select>
      </div>

      {/* Alertes de Fraude */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center mb-6">
          <div className="bg-red-500 p-2 rounded-lg mr-3">
            <AlertTriangle className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Détection de Fraude IA</h3>
        </div>

        <div className="space-y-4">
          {fraudAlerts.map((alert, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <span className="font-mono text-sm text-gray-600 mr-3">{alert.transaction_id}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(alert.risk_score)}`}>
                    Risque: {Math.round(alert.risk_score * 100)}%
                  </span>
                </div>
                <span className="text-xs text-gray-500">{new Date(alert.timestamp).toLocaleString('fr-FR')}</span>
              </div>
              
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-900 mb-2">Facteurs de risque détectés:</p>
                <div className="flex flex-wrap gap-2">
                  {alert.risk_factors.map((factor, idx) => (
                    <span key={idx} className="px-2 py-1 bg-gray-100 text-xs text-gray-700 rounded">
                      {factor}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">Recommandation:</span>
                <span className={`text-sm font-medium ${alert.risk_score > 0.7 ? 'text-red-600' : 'text-yellow-600'}`}>
                  {alert.recommendation}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prédictions de Volume */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-6">
            <div className="bg-blue-500 p-2 rounded-lg mr-3">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Prédictions de Volume</h3>
          </div>

          <div className="space-y-4">
            {predictions.slice(0, 5).map((pred, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  {getTrendIcon(pred.trend)}
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">{new Date(pred.date).toLocaleDateString('fr-FR')}</p>
                    <p className="text-sm text-gray-600">{pred.predicted_transactions} transactions</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">
                    {formatCurrency(pred.predicted_volume, countryConfig.primaryCurrency)}
                  </p>
                  <p className="text-xs text-gray-500">Confiance: {Math.round(pred.confidence * 100)}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cash Flow Predictions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-6">
            <div className="bg-green-500 p-2 rounded-lg mr-3">
              <DollarSign className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Prédictions Cash Flow</h3>
          </div>

          <div className="space-y-4">
            {cashFlowPredictions.map((pred, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">{pred.period}</h4>
                  <span className="text-xs text-gray-500">Confiance: {Math.round(pred.confidence * 100)}%</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Entrées prévues</p>
                    <p className="font-semibold text-green-600">
                      +{formatCurrency(pred.predicted_inflow, countryConfig.primaryCurrency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Sorties prévues</p>
                    <p className="font-semibold text-red-600">
                      -{formatCurrency(pred.predicted_outflow, countryConfig.primaryCurrency)}
                    </p>
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Flux net:</span>
                    <span className={`font-bold ${pred.net_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {pred.net_flow >= 0 ? '+' : ''}{formatCurrency(pred.net_flow, countryConfig.primaryCurrency)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Patterns & Insights */}
      {patterns && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-6">
            <div className="bg-purple-500 p-2 rounded-lg mr-3">
              <Target className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Patterns & Insights IA</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Clock className="h-5 w-5 text-blue-600 mr-2" />
                <h4 className="font-semibold text-blue-900">Heures de Pic</h4>
              </div>
              <div className="space-y-2">
                {patterns.peak_hours.map((hour: string, index: number) => (
                  <div key={index} className="text-sm text-blue-800 bg-blue-100 px-2 py-1 rounded">
                    {hour}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Calendar className="h-5 w-5 text-green-600 mr-2" />
                <h4 className="font-semibold text-green-900">Jours Actifs</h4>
              </div>
              <div className="space-y-2">
                {patterns.peak_days.map((day: string, index: number) => (
                  <div key={index} className="text-sm text-green-800 bg-green-100 px-2 py-1 rounded">
                    {day}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Globe className="h-5 w-5 text-yellow-600 mr-2" />
                <h4 className="font-semibold text-yellow-900">Tendances Saisonnières</h4>
              </div>
              <div className="space-y-2">
                {Object.entries(patterns.seasonal_trends).map(([period, change]: [string, any], index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-yellow-800">{period}:</span>
                    <span className={`font-medium ${change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                      {change}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(patterns.average_transaction_size, countryConfig.primaryCurrency)}</p>
              <p className="text-sm text-gray-600">Transaction Moyenne</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{patterns.most_active_operator}</p>
              <p className="text-sm text-gray-600">Opérateur le Plus Actif</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{patterns.growth_rate}</p>
              <p className="text-sm text-gray-600">Taux de Croissance</p>
            </div>
          </div>
        </div>
      )}

      {/* Recommandations IA */}
      <div className="bg-gradient-to-r from-[#63d400] to-[#7ee000] rounded-xl shadow-sm p-6 text-white">
        <div className="flex items-center mb-6">
          <Zap className="h-6 w-6 mr-3" />
          <h3 className="text-xl font-semibold">Recommandations IA</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {recommendations.map((rec, index) => (
            <div key={index} className="bg-white bg-opacity-20 rounded-lg p-4">
              <div className="flex items-start">
                <Target className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm">{rec}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdvancedAnalytics;